<template>
  <div class="client-questions-card floating-widget">
    <!-- Widget Header -->
    <div class="card-header">
      <div class="card-title-group">
        <div class="title-icon-badge">
          <i class="fas fa-comments"></i>
        </div>
        <div>
          <h2 class="card-title">Client Questions</h2>
          <p class="card-subtitle">Review and respond to incoming client security questionnaire inquiries</p>
        </div>
      </div>

      <div class="status-summary-pills">
        <span class="summary-pill pending-pill">
          <i class="fas fa-clock"></i> {{ paginationTotal }} Pending
        </span>
      </div>
    </div>

    <!-- Question Queue Toolbar / Controls -->
    <div class="list-toolbar">
      <div class="toolbar-left">
        <span class="toolbar-section-label">
          <i class="fas fa-question-circle me-1"></i> Question Queue
        </span>
      </div>

      <div class="controls">
        <div class="control-group">
          <label for="sort-by-questions">Sort By:</label>
          <select id="sort-by-questions" v-model="sortBy" class="form-select">
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <div class="control-group">
          <label for="rows-per-page-questions">Rows per page:</label>
          <select id="rows-per-page-questions" v-model.number="pagination.pageSize" class="form-select">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="25">25</option>
          </select>
        </div>

        <div class="pagination">
          <button type="button" @click="prevPage" :disabled="pagination.currentPage === 1" class="pagination-arrow">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="pagination-label">
            <strong>{{ paginationRange }}</strong> of {{ paginationTotal }}
          </span>
          <button type="button" @click="nextPage" :disabled="pagination.currentPage >= totalPages" class="pagination-arrow">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="questions-list">
      <div v-if="isLoading" class="loading-state">
        <i class="fas fa-spinner fa-spin me-2"></i> Loading client questions...
      </div>
      <div v-else-if="questions.length === 0" class="empty-state">
        <i class="fas fa-inbox empty-icon"></i>
        <p class="empty-title">No pending client questions.</p>
        <p class="empty-sub">All client inquiries have been answered.</p>
      </div>
      <div
        v-else
        v-for="question in questions"
        :key="question.id"
        class="question-row clickable-row"
        @click="viewQuestion(question)"
      >
        <div class="question-info-wrapper">
          <div class="question-date">
            <div class="date-formatted">{{ formatDate(question.created_at) }}</div>
            <div class="time-formatted">{{ formatTime(question.created_at) }}</div>
          </div>

          <div class="question-info">
            <div class="question-top-line">
              <span class="org-name">
                {{ question.proposal?.Lead?.auth_organisation_name || question.proposal?.Lead?.organisation_name }}
              </span>
              <span class="proposal-pill" v-if="question.proposal?.proposal_title">
                <i class="fas fa-file-contract"></i>
                {{ question.proposal.proposal_title }}
              </span>
              <span class="status-badge" :class="question.status === 0 ? 'badge-pending' : 'badge-resolved'">
                <i :class="question.status === 0 ? 'fas fa-hourglass-half' : 'fas fa-check'"></i>
                {{ getQuestionStatusText(question.status) }}
              </span>
            </div>

            <div class="question-text" :title="question.question">
              {{ truncateText(question.question, 140) }}
            </div>

            <div class="question-meta">
              <template v-if="['Main Lead', 'Primary Contact', ''].includes(question.question_by_email_role)">
                <span class="sub-label-gray">
                  <i class="far fa-user me-1"></i>
                  {{ question.question_by_email || question.proposal?.Lead?.email }}
                  <strong>({{ question.question_by_email_role || 'Main Lead' }})</strong>
                </span>
              </template>
              <template v-else>
                <span class="sub-label-gray">
                  <i class="far fa-user me-1"></i>
                  {{ question.question_by_email }}
                  <strong>(Shared Access User - {{ question.question_by_email_role || 'User' }})</strong>
                </span>
              </template>
              <i
                class="fas fa-copy copy-icon"
                @click.stop="copyEmail(question.question_by_email || question.proposal?.Lead?.email)"
                title="Copy Email"
              ></i>
            </div>
          </div>
        </div>

        <div class="question-action">
          <button
            type="button"
            class="action-btn-circle action-btn-circle--view position-relative"
            title="View Question"
            @click.stop="viewQuestion(question)"
          >
            <i class="fas fa-eye"></i>
            <span v-if="!question.is_opened" class="exclamation-icon">
              <i class="fa-solid fa-circle-exclamation"></i>
            </span>
          </button>
          <button
            type="button"
            class="action-btn-circle action-btn-circle--resolve"
            title="Mark as Resolved"
            :disabled="!canManageQuestions(question)"
            @click.stop="resolveQuestion(question.id)"
          >
            <i class="fas fa-check"></i>
          </button>
          <button
            type="button"
            :disabled="!canManageQuestions(question)"
            class="action-btn-circle action-btn-circle--reply"
            title="Send Reply"
            @click.stop="sendReply(question)"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div v-if="isModalOpen" class="modal-backdrop-custom modal-overlay" @click="closeModalOnOutsideClick">
      <div class="modal-content-custom" @click.stop>
        <div class="modal-header-custom">
          <div class="modal-header-title-wrap">
            <div class="modal-icon-circle">
              <i class="fas fa-comments"></i>
            </div>
            <div>
              <h5 class="modal-title-custom">Question Details</h5>
              <p class="modal-subtitle-custom">Client proposal inquiry details</p>
            </div>
          </div>
          <button type="button" @click="isModalOpen = false" class="close-button-custom">&times;</button>
        </div>
        <div class="modal-body-custom" v-if="currentQuestion">
          <p>
            <strong>Organisation:</strong>
            {{ currentQuestion.proposal?.Lead?.auth_organisation_name || currentQuestion.proposal?.Lead?.organisation_name }}
          </p>
          <p>
            <strong>Questioned By:</strong>
            {{ currentQuestion.question_by_email || currentQuestion.proposal?.Lead?.email }}
            (<strong>{{ currentQuestion.question_by_email_role || 'Main Lead' }}</strong>)
            <i
              class="fas fa-copy copy-icon"
              @click.stop="copyEmail(currentQuestion.question_by_email || currentQuestion.proposal?.Lead?.email)"
            ></i>
          </p>
          <p>
            <strong>Account Type:</strong>
            {{ ['Main Lead', 'Primary Contact', ''].includes(currentQuestion.question_by_email_role) ? 'Primary Contact' : 'Shared Access User' }}
          </p>
          <p><strong>Proposal:</strong> {{ currentQuestion.proposal?.proposal_title }}</p>
          <hr />
          <p><strong>Question:</strong></p>
          <div class="full-question-box">{{ currentQuestion.question }}</div>
        </div>
      </div>
    </div>
  </Teleport>

  <ConfirmModal
    v-model="showConfirmModal"
    :title="confirmModalTitle"
    :message="confirmModalMessage"
    @confirm="onConfirmAction"
  />
</template>

<script>
import { truncateLongText } from '@/plugins/helper.js';

export default {
  name: 'ClientQuestionsWidget',
  emits: ['count-updated'],
  data() {
    return {
      isLoading: false,
      questions: [],
      sortBy: 'newest',
      pagination: {
        currentPage: 1,
        pageSize: 5,
        totalItems: 0,
      },
      isModalOpen: false,
      currentQuestion: null,
      colorForStatus: { pending: '#F3994D', resolved: '#00A54F' },
      showConfirmModal: false,
      confirmModalTitle: '',
      confirmModalMessage: '',
      pendingActionId: null,
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.pagination.totalItems / this.pagination.pageSize));
    },
    paginationRange() {
      const total = this.pagination.totalItems;
      if (total === 0) return '0';
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize + 1;
      const end = Math.min(start + this.pagination.pageSize - 1, total);
      return `${start}-${end}`;
    },
    paginationTotal() {
      return this.pagination.totalItems;
    },
  },
  watch: {
    'pagination.pageSize'() {
      this.pagination.currentPage = 1;
      this.fetchQuestions();
    },
    'pagination.currentPage'() {
      this.fetchQuestions();
    },
    sortBy() {
      this.pagination.currentPage = 1;
      this.fetchQuestions();
    },
  },
  created() {
    this.fetchQuestions();
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return 'N/A';
      const options = { year: 'numeric', month: 'short', day: '2-digit' };
      return date.toLocaleDateString('en-US', options);
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
    truncateText(text, length) {
      return truncateLongText(text, length);
    },
    getQuestionStatusText(status) {
      return status === 0 ? 'Pending' : 'Resolved';
    },
    getStatusColor(status) {
      return status === 0 ? this.colorForStatus.pending : this.colorForStatus.resolved;
    },
    async fetchQuestions() {
      this.isLoading = true;
      try {
        const sortOrder = this.sortBy === 'newest' ? 'desc' : 'asc';
        const response = await this.$axios.get('/questions/get-all', {
          params: {
            page: this.pagination.currentPage,
            pageSize: this.pagination.pageSize,
            sortBy: 'created_at',
            sortOrder: sortOrder,
            status: 0,
          },
        });
        if (response.data.success) {
          this.questions = response.data.questions || [];
          this.pagination.totalItems = response.data.totalItems || 0;
        }
      } catch (error) {
        console.error('Failed to fetch questions:', error);
      } finally {
        this.isLoading = false;
        this.$emit('count-updated', this.pagination.totalItems || 0);
      }
    },
    viewQuestion(question) {
      this.currentQuestion = question;
      this.isModalOpen = true;

      if (!question.is_opened) {
        this.markQuestionAsOpened(question.id);
      }
    },
    async markQuestionAsOpened(questionId) {
      try {
        const response = await this.$axios.put(`/questions/${questionId}/mark-opened`);
        if (response.data.success) {
          const index = this.questions.findIndex((q) => q.id === questionId);
          if (index !== -1) {
            this.questions[index].is_opened = true;
          }
        }
      } catch (error) {
        console.error('Error marking question as opened:', error);
      }
    },
    async resolveQuestion(questionId) {
      this.pendingActionId = questionId;
      this.confirmModalTitle = 'Resolve Question';
      this.confirmModalMessage = 'Are you sure you want to mark this question as resolved?';
      this.showConfirmModal = true;
    },
    async onConfirmAction() {
      const questionId = this.pendingActionId;
      this.pendingActionId = null;
      if (!questionId) return;
      try {
        const response = await this.$axios.put(`/questions/${questionId}/resolve`);
        if (response.data.success) {
          this.$toast.success('Question resolved successfully.');
          this.fetchQuestions();
        }
      } catch (error) {
        this.$toast.error('An error occurred while resolving the question.');
      }
    },
    closeModalOnOutsideClick(event) {
      if (event.target.classList.contains('modal-backdrop-custom')) {
        this.isModalOpen = false;
      }
    },
    async copyEmail(email) {
      try {
        await navigator.clipboard.writeText(email);
        this.$toast.success('Email copied to clipboard!');
      } catch (err) {
        this.$toast.error('Failed to copy email.');
      }
    },
    nextPage() {
      if (this.pagination.currentPage < this.totalPages) this.pagination.currentPage++;
    },
    prevPage() {
      if (this.pagination.currentPage > 1) this.pagination.currentPage--;
    },
    canManageQuestions(question) {
      if (this.$store.state.auth.isAdmin) {
        return false;
      }
      if (this.$store.getters['auth/isGlobalSuperAdmin']) {
        return true;
      }
      if (this.$store.state.auth.isConsultant) {
        const currentUserId = this.$store.state.auth.currentUser?.id || 0;
        const creatorId = question.proposal?.Lead?.created_by || question.proposal?.created_by || 0;
        return creatorId > 0 && currentUserId === creatorId;
      }
      return false;
    },
    sendReply(question) {
      if (!this.canManageQuestions(question)) return;
      const email = question.question_by_email || question.proposal?.Lead?.email;
      if (email) {
        window.location.href = `mailto:${email}?subject=RE: ${encodeURIComponent(question.proposal?.proposal_title || '')}`;
      }
    },
  },
};
</script>

<style scoped>
.client-questions-card {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  padding: 0;
  font-family: 'Roboto', sans-serif;
  width: 100%;
  max-width: 100%;
}

.floating-widget {
  position: relative;
  width: 100%;
  z-index: 10;
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.card-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.title-icon-badge {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background-color: #DAF3F9;
  color: #00A0C1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #435666;
  margin: 0;
}

.card-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #5C768B;
}

.status-summary-pills {
  display: flex;
  align-items: center;
  gap: 10px;
}

.summary-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 0.82rem;
  font-weight: 700;
  border: 1px solid transparent;
}

.pending-pill {
  background-color: #FFDEC3;
  color: #F3994D;
}

/* List Toolbar */
.list-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px;
  border-bottom: 1px solid #e9ecef;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.toolbar-section-label {
  font-size: 0.95rem;
  font-weight: 700;
  color: #435666;
}

/* Controls */
.controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group label {
  color: #5C768B;
  font-size: 0.88rem;
  font-weight: 500;
  white-space: nowrap;
}

.form-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 16px 12px;
  padding: 6px 36px 6px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.88rem;
  font-weight: 500;
  color: #435666;
  background-color: #fff;
}

/* Pagination */
.pagination {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
}

.pagination-label {
  font-size: 0.88rem;
  color: #5C768B;
  font-weight: 500;
  padding: 0 8px;
  white-space: nowrap;
}

.pagination-label strong {
  color: #435666;
}

.pagination-arrow {
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #00A0C1;
  font-size: 0.95rem;
  padding: 6px 12px;
  transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #f1f3f5;
}

.pagination-arrow:disabled {
  color: #ced4da;
  cursor: not-allowed;
}

/* SCROLLABLE LIST CONTAINER */
.questions-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px 24px;
  -webkit-overflow-scrolling: touch;
}

.questions-list::-webkit-scrollbar {
  width: 6px;
}
.questions-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
  margin: 10px 0;
}
.questions-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}
.questions-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.question-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:hover {
  border-color: #00A0C1;
  box-shadow: 0 3px 10px rgba(0, 160, 193, 0.08);
  background-color: #fcfdfe;
}

.question-info-wrapper {
  display: flex;
  align-items: stretch;
  flex-grow: 1;
  min-width: 0;
}

.question-date {
  background-color: #435666;
  color: #ffffff;
  min-width: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 12px;
  text-align: center;
  flex-shrink: 0;
}

.date-formatted {
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
}

.time-formatted {
  font-size: 0.78rem;
  color: #dbe4ec;
  margin-top: 2px;
}

.question-info {
  flex-grow: 1;
  padding: 14px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.question-top-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.org-name {
  font-weight: 700;
  font-size: 1.05rem;
  color: #435666;
}

.proposal-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 600;
  background-color: #e6f6f9;
  color: #00829e;
  border: 1px solid #b6e6f2;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.74rem;
  padding: 3px 10px;
  border-radius: 9999px;
  font-weight: 700;
  text-transform: uppercase;
}

.badge-pending {
  background-color: #FFDEC3;
  color: #F3994D;
}

.badge-resolved {
  background-color: #D1FAE5;
  color: #00A54F;
}

.question-text {
  font-size: 0.9rem;
  color: #435666;
  background-color: #f8fafc;
  border-left: 3px solid #00A0C1;
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
  line-height: 1.4;
  word-break: break-word;
}

.question-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sub-label-gray {
  font-size: 0.8rem;
  color: #5C768B;
}

.copy-icon {
  cursor: pointer;
  color: #94a3b8;
  font-size: 0.82rem;
  padding: 4px;
  transition: 0.2s;
}

.copy-icon:hover {
  color: #00A0C1;
  background-color: #e9ecef;
  border-radius: 4px;
}

.question-action {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  flex-shrink: 0;
}

.position-relative {
  position: relative;
}

.exclamation-icon {
  position: absolute;
  top: -4px;
  right: -4px;
  color: #F3994D;
  font-size: 1em;
  background-color: white;
  border-radius: 50%;
  line-height: 1;
}

.action-btn-circle {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  padding: 0;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  position: relative;
}

.action-btn-circle--view {
  background-color: #f1f5f9;
  color: #435666;
}

.action-btn-circle--view:hover:not(:disabled) {
  background-color: #435666;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(67, 86, 102, 0.25);
}

.action-btn-circle--resolve {
  background-color: #D1FAE5;
  color: #00A54F;
}

.action-btn-circle--resolve:hover:not(:disabled) {
  background-color: #00A54F;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 165, 79, 0.25);
}

.action-btn-circle--reply {
  background-color: #e6f7fa;
  color: #00A0C1;
}

.action-btn-circle--reply:hover:not(:disabled) {
  background-color: #00A0C1;
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.action-btn-circle:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Empty & Loading States */
.loading-state,
.empty-state {
  text-align: center;
  padding: 36px 20px;
  color: #5C768B;
}

.empty-icon {
  font-size: 2rem;
  color: #cbd5e1;
  margin-bottom: 8px;
  display: block;
}

.empty-title {
  font-weight: 600;
  color: #435666;
  margin: 0 0 4px 0;
}

.empty-sub {
  font-size: 0.85rem;
  color: #5C768B;
  margin: 0;
}

/* MODAL STYLES */
.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(29, 43, 54, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 16px;
}

.modal-content-custom {
  position: relative;
  background-color: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
}

.modal-header-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header-title-wrap {
  display: flex;
  align-items: center;
  gap: 14px;
}

.modal-icon-circle {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background-color: #DAF3F9;
  color: #00A0C1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.modal-title-custom {
  font-weight: 700;
  font-size: 1.15rem;
  color: #435666;
  margin: 0;
}

.modal-subtitle-custom {
  font-size: 0.82rem;
  color: #5C768B;
  margin: 2px 0 0 0;
}

.close-button-custom {
  font-size: 1.8rem;
  line-height: 1;
  color: #5C768B;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
}

.close-button-custom:hover {
  color: #435666;
}

.modal-body-custom {
  padding: 20px 24px 24px;
  color: #435666;
  font-size: 0.92rem;
}

.modal-body-custom p {
  margin-bottom: 10px;
}

.full-question-box {
  background-color: #f8fafc;
  border-left: 3px solid #00A0C1;
  padding: 16px;
  border-radius: 0 10px 10px 0;
  border-top: 1px solid #e9ecef;
  border-right: 1px solid #e9ecef;
  border-bottom: 1px solid #e9ecef;
  white-space: pre-wrap;
  font-size: 0.95rem;
  color: #435666;
  line-height: 1.6;
}
</style>